import { Colors } from "@/enums/Colors";
import { Spinner } from "@/external/ensolvers-core-frontend-mui-v5/components/Common/Spinner";
import { SearchHistory } from "@/models/SearchHistory";
import { DataRoomFile } from "@/models/data-room/DataRoomFile";
import { VirtualDataRoomFile } from "@/models/virtual-data-room/VirtualDataRoomFile";
import { DataRoomEntry } from "@/pages/OneDataRoomPage";
import { EnumsUtils } from "@/utils/TaskEnumUtils";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Popper from "@mui/material/Popper";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useEffect, useRef } from "react";
import { FilesIcon } from "../utils/icons/FilesIcon";
import { FolderNotch } from "../utils/icons/FolderNotch";
import OpenInNewIcon from "../utils/icons/OpenInNewIcon";

type Props = {
  menuAnchor: HTMLElement;
  setMenuAnchor: (el: HTMLInputElement) => void;
  inputValue: string;
  loading: boolean;
  menuOptions?: (VirtualDataRoomFile | DataRoomEntry | DataRoomFile)[];
  onClickMenuOpt: (opt: any) => void;
  onInputChange: (ev: React.ChangeEvent<HTMLInputElement>) => void;
  searchHistory: SearchHistory[];
};

function renderOption(opt: VirtualDataRoomFile | DataRoomEntry | DataRoomFile) {
  let icon = <FilesIcon sx={{ fill: Colors.ACCENT_ORANGE }} />;
  let pillColor = Colors.ACCENT_ORANGE;
  let type = null;
  if ("type" in opt) {
    // VirtualDataRoomFile
    type = opt.type;
    switch (opt.type) {
      case "FOLDER": {
        icon = <FolderNotch sx={{ fill: Colors.LAZO_PURPLE }} />;
        pillColor = Colors.LAZO_PURPLE;
        break;
      }
      case "URL": {
        icon = <OpenInNewIcon />;
        pillColor = Colors.BLUE_4763F4;
        break;
      }
      default: {
        break;
      }
    }
  } else if ("suggestedStaticId" in opt) {
    // DataRoomFile
    type = "FILE";
  } else {
    // DataRoomEntry
    type = "FOLDER";
    icon = <FolderNotch sx={{ fill: Colors.LAZO_PURPLE }} />;
    pillColor = Colors.LAZO_PURPLE;
  }
  return (
    <>
      <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
        <Box display="flex" alignItems="center" gap="0.5rem">
          {icon}
          <Box display="flex" alignItems="center" flexDirection="column">
            <Typography
              fontSize="0.875rem"
              color={Colors.BLACK_2}
              maxWidth="32rem"
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
            >
              {opt.name}
            </Typography>
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            padding: "0.125rem 0.5rem",
            borderRadius: "100px",
            border: `1px solid ${pillColor}`,
          }}
        >
          <Typography fontSize="0.625rem" fontWeight="400" color={Colors.BLACK_2}>
            {EnumsUtils.parseEnumToRender(type)}
          </Typography>
        </Box>
      </Box>
    </>
  );
}

function DataRoomSearchMenu(props: Props) {
  const popperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onMouseDown(ev: MouseEvent) {
      if (ev.target === props.menuAnchor || popperRef.current?.contains(ev.target as Node)) return;
      props.setMenuAnchor(null);
    }
    document.addEventListener("mousedown", onMouseDown);
    return () => document.removeEventListener("mousedown", onMouseDown);
  }, [props.menuAnchor, popperRef]);

  if (props.loading) {
    return (
      <Popper
        anchorEl={props.menuAnchor}
        open={Boolean(props.menuAnchor)}
        disablePortal={true}
        placement={"bottom-start"}
        ref={popperRef}
        sx={{ zIndex: 1200 }}
      >
        <StyledMenuList>
          <Spinner size={"2rem"} sx={{ color: Colors.LAZO_PURPLE }} />
        </StyledMenuList>
      </Popper>
    );
  }

  return (
    <>
      <Popper
        anchorEl={props.menuAnchor}
        open={Boolean(props.menuAnchor)}
        disablePortal={true}
        placement={"bottom-start"}
        ref={popperRef}
        sx={{ zIndex: 1200 }}
      >
        {props.searchHistory?.length > 0 && props.inputValue === "" ? (
          <StyledMenuList>
            <MenuItem disabled={true} key={"search-hint"}>
              <Typography fontSize="0.875rem" color={Colors.BLACK}>
                Search history
              </Typography>
            </MenuItem>
            {props.searchHistory.map((o, idx) => (
              <MenuItem
                key={idx}
                onClick={() => {
                  props.onInputChange({ target: { value: o.search } } as any);
                }}
              >
                <Typography
                  fontSize="0.875rem"
                  color={Colors.BLACK_2}
                  maxWidth="32rem"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                  overflow="hidden"
                >
                  {o.search}
                </Typography>
              </MenuItem>
            ))}
          </StyledMenuList>
        ) : (
          <StyledMenuList>
            {props.menuOptions?.length === 0 ? (
              <Typography>No matches</Typography>
            ) : (
              props.menuOptions?.map((o, idx) => (
                <MenuItem
                  key={idx}
                  onClick={() => {
                    props.onClickMenuOpt(o);
                    props.onInputChange({ target: { value: "" } } as any);
                  }}
                >
                  {renderOption(o)}
                </MenuItem>
              ))
            )}
          </StyledMenuList>
        )}
      </Popper>
    </>
  );
}

const StyledMenuList = styled(MenuList)(() => ({
  display: "flex",
  alignItems: "center",
  flexDirection: "column",
  backgroundColor: "#fff",
  borderRadius: "0.5rem",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  width: "40rem",
  boxShadow: "0 0 19px 0 rgba(0, 0, 0, 0.1)",
  maxHeight: "70vh",
  overflow: "hidden",
  overflowY: "auto",
  "& .MuiMenuItem-root": {
    width: "100%",
    padding: "0.75rem 1rem",
  },
}));

export default DataRoomSearchMenu;
